"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTheme } from "next-themes";
import {
  FaDesktop,
  FaEnvelope,
  FaGithub,
  FaMoon,
  FaSun,
  FaXTwitter,
} from "react-icons/fa6";

import { FittyText } from "@/components/fitty-text";
import { oswald } from "@/styles/fonts";
import { cn } from "@/utils/cn";
import { BrandText } from "./brand-text";

export const Footer = () => {
  const { setTheme, theme } = useTheme();
  const pathname = usePathname();

  const [currentTimeNigeria, setCurrentTimeNigeria] = useState("00:00");
  const [isLoadingTime, setIsLoadingTime] = useState(true);

  // Update the time every 1 second
  useEffect(() => {
    let isMounted = true;

    const updateTime = () => {
      const now = new Date();
      const nigeriaTime = new Date(
        now.toLocaleString("en-US", { timeZone: "Africa/Lagos" }),
      );
      const hours = nigeriaTime.getHours().toString().padStart(2, "0");
      const minutes = nigeriaTime.getMinutes().toString().padStart(2, "0");
      const formattedTime = `${hours}:${minutes}`;

      if (isMounted) {
        setCurrentTimeNigeria(formattedTime);
        if (isLoadingTime) {
          setIsLoadingTime(false);
        }
      }
    };

    updateTime();
    const intervalId = setInterval(updateTime, 1000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [isLoadingTime]);

  return (
    <footer className="container text-[11px] text-neutral-500 xs:text-xs dark:text-neutral-500">
      <div className="flex flex-col gap-8 pb-8 md:gap-12 md:pb-12">
        <FittyText
          className={cn(
            "fit-text cursor-default transition-colors duration-300 ease-in-out lg:hover:text-royal-500 dark:lg:hover:text-gold-500",
            oswald.className,
          )}
        >
          <p className="cursor-default">
            {PAGES.find((page) => page.href === pathname)?.name || "gozman.dev"}
          </p>
        </FittyText>

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            {/* Social Links */}
            <div className="flex items-center gap-2.5">
              <Link
                href={"https://github.com/gozmanthefirst"}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 ease-in-out hover:text-royal-500 dark:hover:text-gold-500"
              >
                <FaGithub className="size-4" />
              </Link>

              <div className="h-3 w-px bg-neutral-300 dark:bg-neutral-700" />

              <Link
                href={"https://x.com/gozmanthefirst"}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 ease-in-out hover:text-royal-500 dark:hover:text-gold-500"
              >
                <FaXTwitter className="size-4" />
              </Link>

              <div className="h-3 w-px bg-neutral-300 dark:bg-neutral-700" />

              <Link
                href={"mailto:gozmansunday@gmail.com"}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 ease-in-out hover:text-royal-500 dark:hover:text-gold-500"
              >
                <FaEnvelope className="size-4" />
              </Link>
            </div>

            {/* Theme Switch */}
            <div className="flex items-center gap-4">
              <div className={cn("flex items-center gap-2")}>
                {theme && !isLoadingTime ? (
                  <>
                    <FaSun
                      className={cn(
                        "size-4 cursor-pointer transition-colors duration-200 ease-in-out lg:hover:text-neutral-900 dark:lg:hover:text-neutral-100",
                        theme === "light"
                          ? "text-royal-500 lg:hover:text-royal-500 dark:text-gold-500 dark:lg:hover:text-gold-500"
                          : "",
                      )}
                      onClick={() => setTheme("light")}
                    />
                    <FaMoon
                      className={cn(
                        "size-4 cursor-pointer transition-colors duration-200 ease-in-out lg:hover:text-neutral-900 dark:lg:hover:text-neutral-100",
                        theme === "dark"
                          ? "text-royal-500 lg:hover:text-royal-500 dark:text-gold-500 dark:lg:hover:text-gold-500"
                          : "",
                      )}
                      onClick={() => setTheme("dark")}
                    />
                    <FaDesktop
                      className={cn(
                        "size-4 cursor-pointer transition-colors duration-200 ease-in-out lg:hover:text-neutral-900 dark:lg:hover:text-neutral-100",
                        theme === "system"
                          ? "text-royal-500 lg:hover:text-royal-500 dark:text-gold-500 dark:lg:hover:text-gold-500"
                          : "",
                      )}
                      onClick={() => setTheme("system")}
                    />
                  </>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex w-full flex-row items-end justify-between gap-4">
            {/* Left section */}
            <div className="flex flex-col items-start gap-0.5">
              <p>
                <span>
                  &copy; {new Date().getFullYear()}{" "}
                  <BrandText className="hidden sm:inline">
                    Chiagoziem Sunday.
                  </BrandText>
                  <BrandText className="sm:hidden">Chiagoziem S.</BrandText>
                </span>
              </p>
            </div>

            {/* Right section */}
            <div className="flex flex-col items-end gap-0.5 whitespace-nowrap">
              <p>
                <span
                  className={cn(
                    "transition-filter duration-300 ease-in-out",
                    isLoadingTime ? "blur-xs" : "blur-none",
                  )}
                >
                  <BrandText>{currentTimeNigeria}</BrandText> •{" "}
                </span>
                Port Harcourt, NG
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const PAGES = [
  {
    name: "gozman.dev",
    href: "/",
  },
  {
    name: "gozman.dev/spotify",
    href: "/spotify",
  },
];
